// The type of a file as shown in the Type column of the file list. It is
// made from the extension of the name alone: nothing is read from the file.

const typeNames: Record<string, string> = {
  // text and documents
  txt: "Text",
  log: "Log",
  md: "Markdown",
  markdown: "Markdown",
  rtf: "RTF",
  pdf: "PDF",
  doc: "Word",
  docx: "Word",
  odt: "OpenDocument",
  xls: "Excel",
  xlsx: "Excel",
  ods: "OpenDocument",
  csv: "CSV",
  tsv: "TSV",
  ppt: "PowerPoint",
  pptx: "PowerPoint",
  odp: "OpenDocument",
  epub: "EPUB",
  mobi: "Mobi",
  djvu: "DjVu",
  // images
  jpg: "JPEG",
  jpeg: "JPEG",
  jpe: "JPEG",
  png: "PNG",
  gif: "GIF",
  webp: "WebP",
  avif: "AVIF",
  heic: "HEIC",
  heif: "HEIF",
  bmp: "BMP",
  tif: "TIFF",
  tiff: "TIFF",
  svg: "SVG",
  ico: "Icon",
  psd: "Photoshop",
  // audio and video
  mp3: "MP3",
  m4a: "M4A",
  aac: "AAC",
  wav: "WAV",
  flac: "FLAC",
  ogg: "Ogg",
  opus: "Opus",
  mp4: "MP4",
  m4v: "MP4",
  mov: "QuickTime",
  mkv: "Matroska",
  webm: "WebM",
  avi: "AVI",
  wmv: "WMV",
  // archives and images of disks
  zip: "ZIP",
  "7z": "7-Zip",
  rar: "RAR",
  tar: "Tar",
  gz: "Gzip",
  tgz: "Gzip",
  bz2: "Bzip2",
  xz: "XZ",
  zst: "Zstandard",
  iso: "Disk image",
  dmg: "Disk image",
  // the web and data
  htm: "HTML",
  html: "HTML",
  css: "CSS",
  js: "JavaScript",
  mjs: "JavaScript",
  ts: "TypeScript",
  jsx: "JavaScript",
  tsx: "TypeScript",
  json: "JSON",
  xml: "XML",
  yaml: "YAML",
  yml: "YAML",
  toml: "TOML",
  ini: "INI",
  sql: "SQL",
  // code
  go: "Go",
  py: "Python",
  rb: "Ruby",
  rs: "Rust",
  java: "Java",
  c: "C",
  h: "C header",
  cpp: "C++",
  cc: "C++",
  hpp: "C++ header",
  cs: "C#",
  php: "PHP",
  swift: "Swift",
  kt: "Kotlin",
  sh: "Shell script",
  bat: "Batch file",
  ps1: "PowerShell",
  svelte: "Svelte",
  // programs and what comes with them
  exe: "Program",
  msi: "Installer",
  dll: "DLL",
  apk: "APK",
  deb: "Debian package",
  rpm: "RPM package",
  ttf: "Font",
  otf: "Font",
  woff: "Font",
  woff2: "Font",
};

// Returns the extension of a name, lower case and without the dot, or "" if
// it has none. A name that only starts with a dot, like ".gitignore", has
// none.
export function fileExt(name: string): string {
  let i = name.lastIndexOf(".");
  if (i <= 0 || i == name.length - 1) {
    return "";
  }
  return name.substring(i + 1).toLowerCase();
}

// Returns the type to show for a file or a directory: a name we know for the
// extension, else the extension in capitals, else "" for a file without one.
export function fileTypeName(name: string, isDir: boolean): string {
  if (isDir) {
    return name == ".." ? "" : "Folder";
  }
  let ext = fileExt(name);
  if (ext == "") {
    return "";
  }
  // not a property of every object, like "valueOf"
  if (Object.hasOwn(typeNames, ext)) {
    return typeNames[ext];
  }
  if (ext.length > 10 || !/^[a-z0-9_+-]+$/.test(ext)) {
    // not an extension: a dot somewhere in a name
    return "";
  }
  return ext.toUpperCase();
}

// The icons of the file list: there is an SVG file for each in
// icons/filetypes, see file-icons.ts.
export const fileIconKinds = [
  "folder",
  "up",
  "file",
  "text",
  "pdf",
  "image",
  "video",
  "audio",
  "archive",
  "code",
  "doc",
  "sheet",
  "slides",
  "ebook",
  "program",
  "font",
  "disk",
  // not a kind of file: a row selected together with others
  "checked",
] as const;

export type FileIconKind = (typeof fileIconKinds)[number];

const iconExts: [FileIconKind, string][] = [
  ["text", "txt log md markdown rtf ini toml yaml yml nfo"],
  ["pdf", "pdf"],
  ["image", "jpg jpeg jpe png gif webp avif heic heif bmp tif tiff svg ico psd raw cr2 nef dng"],
  ["video", "mp4 m4v mov mkv webm avi wmv flv mpg mpeg 3gp"],
  ["audio", "mp3 m4a aac wav flac ogg opus wma aiff mid midi"],
  ["archive", "zip 7z rar tar gz tgz bz2 xz zst cab jar"],
  [
    "code",
    "htm html css js mjs jsx ts tsx json xml sql go py rb rs java c h cpp cc hpp cs php swift kt sh bat ps1 svelte",
  ],
  ["doc", "doc docx odt"],
  ["sheet", "xls xlsx ods csv tsv"],
  ["slides", "ppt pptx odp"],
  ["ebook", "epub mobi djvu azw azw3 fb2"],
  ["program", "exe msi dll apk deb rpm app com"],
  ["font", "ttf otf woff woff2"],
  ["disk", "iso dmg img vhd vmdk"],
];

const iconOfExt: Record<string, FileIconKind> = {};
for (let [kind, exts] of iconExts) {
  for (let ext of exts.split(" ")) {
    iconOfExt[ext] = kind;
  }
}

// Returns the icon to show for a file or a directory, from its name alone.
export function fileIconKind(name: string, isDir: boolean): FileIconKind {
  if (isDir) {
    return name == ".." ? "up" : "folder";
  }
  let ext = fileExt(name);
  if (ext != "" && Object.hasOwn(iconOfExt, ext)) {
    return iconOfExt[ext];
  }
  return "file";
}
